/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoSettingsModalSource } from '@atlassian/rovo-settings-modal/common/utils/rovo-settings-config';
import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';

import { ProductKeys3P } from '../../../../common/constants/3p-product-configs/product-keys-3p';
import { universalTypeFilterId } from '../../../../common/constants/filters/types';
import { isNounKey, NounKeys } from '../../../../common/constants/nouns';
import { ProductKeys, useProductConfigs } from '../../../../common/constants/products';
import type { TriggerComponentProps } from '../../../../common/types';
import ShowMoreAppsPopup from '../../../../common/ui/filters/app-filter-sidebar/show-more-apps-popup';
import { type FilterOption } from '../../../../common/ui/filters/app-filter-sidebar/show-more-apps-popup';
import { OAuthWarningWrapper } from '../../../../common/ui/oauth-warning-wrapper';
import { ProductOrNounIcon } from '../../../../common/ui/product-icon';
import {
	type AppOption,
	buildNounOptions,
	buildProductOptions,
	filterProductsForNouns,
	getShouldShowNouns,
	splitByAuthStatus,
} from '../../../../common/utils/app-filter-options';
import { isProductSmartlink } from '../../../../common/utils/connectors';
import { OAuthSource } from '../../../../common/utils/events/oauth-events';
import {
	serveSearchSettingsModalExperiment,
	triggerExposureForSearchSettingsModalExperiment,
} from '../../../../common/utils/experiments';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../controllers/oauth';
import { useRightPanelRecommendations } from '../../../../controllers/recommened3pApps';
import {
	useBootstrap,
	useCurrentUserId,
	useIsNounsAvailable,
} from '../../../../controllers/store/bootstrap';
import {
	useFilters,
	useSearchActions,
	useSelectedProduct,
} from '../../../../controllers/store/search';

import { getScrollAmount as getScrollAmountFromLayout } from './get-scroll-amount';

/**
 * Describes how the visible slot budget for the Lite app pill bar is split between
 * authenticated/connected apps (1P + 3P) and unauthenticated 3P apps.
 *
 * Apps are emitted in this order:
 *   firstConnectedBatchSize → unauthenticated3pSlots → secondConnectedBatchSize
 */
type AppPillLayout = {
	firstConnectedBatchSize: number;
	unauthenticated3pSlots: number;
	secondConnectedBatchSize: number;
};

/**
 * Default layout: 4 connected (1P + 3P) → 2 unauthenticated 3P → 3 connected.
 * Total visible pills = 9.
 */
const DEFAULT_APP_PILL_LAYOUT: AppPillLayout = {
	firstConnectedBatchSize: 4,
	unauthenticated3pSlots: 2,
	secondConnectedBatchSize: 3,
};

/**
 * Team 26 Demo layout: 6 connected (1P + 3P) → 3 unauthenticated 3P (no second connected batch).
 * Total visible pills = 9. Gated behind `rovo_fps_lite_app_filter_team26_demo_layout` and intended
 * to be targeted at Team 26 Demo sites via Statsig cohorting.
 */
const TEAM26_DEMO_APP_PILL_LAYOUT: AppPillLayout = {
	firstConnectedBatchSize: 6,
	unauthenticated3pSlots: 3,
	secondConnectedBatchSize: 0,
};

/**
 * Selects the active {@link AppPillLayout}. This is the single place the layout-related
 * feature gate(s) are consulted; downstream callers only deal with the resulting layout.
 */
export const getAppPillLayout = (): AppPillLayout => {
	if (fg('rovo_fps_lite_app_filter_team26_demo_layout')) {
		return TEAM26_DEMO_APP_PILL_LAYOUT;
	}
	return DEFAULT_APP_PILL_LAYOUT;
};

/**
 * Applies a {@link AppPillLayout} to the given authenticated/unauthenticated app option lists,
 * producing the visible pill order and the overflow remainder.
 *
 * Pure function (no React/feature-gate access) so it can be tested and reused independently.
 */
/**
 * Pinned product/app order applied to authenticated app options when the
 * `rovo_fps_lite_app_filter_team26_demo_layout` feature gate is on. Entries are placed in this
 * exact order at the front of the visible authenticated list. Any pinned key that is not
 * present in `authenticatedOptions` (e.g. an unauthenticated 3P, or a product not available
 * for the current user) is skipped — it is not added to a fallback position. Authenticated
 * apps not in the pinned list keep their original relative order after the pinned prefix.
 */
export const TEAM26_PINNED_ORDER: ReadonlyArray<string> = [
	ProductKeys.Jira,
	ProductKeys3P.GoogleDrive,
	ProductKeys3P.Teams,
	ProductKeys3P.Salesforce,
	ProductKeys3P.Github,
	ProductKeys3P.Figma,
	ProductKeys.Confluence,
];

/**
 * Reorders `authenticatedOptions` so that any entries whose `value` matches `pinnedOrder`
 * appear first in the declared pinned order, followed by the remaining authenticated entries
 * in their original order. Pinned keys with no matching entry are skipped silently.
 *
 * Pure function (no React/feature-gate access) so it can be tested and reused independently.
 */
export const applyPinnedOrder = (
	authenticatedOptions: AppOption[],
	pinnedOrder: ReadonlyArray<string>,
): AppOption[] => {
	const byValue = new Map(authenticatedOptions.map((o) => [o.value as string, o]));
	const pinnedKeys = new Set(pinnedOrder);
	const pinned = pinnedOrder
		.map((key) => byValue.get(key))
		.filter((o): o is AppOption => o !== undefined);
	const rest = authenticatedOptions.filter((o) => !pinnedKeys.has(o.value as string));
	return [...pinned, ...rest];
};

export const applyAppPillLayout = ({
	layout,
	authenticatedOptions,
	rankedUnauthenticated3p,
}: {
	layout: AppPillLayout;
	authenticatedOptions: AppOption[];
	rankedUnauthenticated3p: AppOption[];
}): { visibleOptions: AppOption[]; overflowOptions: AppOption[] } => {
	const { firstConnectedBatchSize, unauthenticated3pSlots, secondConnectedBatchSize } = layout;

	const firstConnectedBatch = authenticatedOptions.slice(0, firstConnectedBatchSize);
	const visibleUnauthenticated3p = rankedUnauthenticated3p.slice(0, unauthenticated3pSlots);
	const secondConnectedBatch = authenticatedOptions.slice(
		firstConnectedBatchSize,
		firstConnectedBatchSize + secondConnectedBatchSize,
	);
	const visibleOptions = [
		...firstConnectedBatch,
		...visibleUnauthenticated3p,
		...secondConnectedBatch,
	];

	const overflowAuthenticated = authenticatedOptions.slice(
		firstConnectedBatchSize + secondConnectedBatchSize,
	);
	const overflowUnauthenticated3p = rankedUnauthenticated3p.slice(unauthenticated3pSlots);
	const overflowOptions = [...overflowAuthenticated, ...overflowUnauthenticated3p];

	return { visibleOptions, overflowOptions };
};

const styles = cssMap({
	pill: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.full'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		cursor: 'pointer',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
		backgroundColor: token('color.background.neutral'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
			color: token('color.text'),
		},
	},
	pillSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.text.selected'),
		},
	},
	scrollContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		overflowX: 'auto',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	outerContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
	},
	chevronButton: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.full'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	chevronContainer: {
		position: 'absolute',
		top: '0',
		bottom: '0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
		paddingInline: token('space.025'),
	},
	chevronLeft: {
		left: '0',
	},
	chevronRight: {
		right: '0',
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		overflow: 'hidden',
		flexShrink: 0,
	},
});

/**
 * Builds the ordered list of visible and overflow app options for the Lite mode pill bar.
 * - Total of up to 9 visible pills, split per the active {@link AppPillLayout}
 *   (see {@link getAppPillLayout} for the selection logic).
 * - Unauthenticated 3P apps are ranked by recommended3pApps order.
 * - Remaining apps beyond the visible budget go to the "Show all apps" overflow dropdown.
 *
 * Uses shared util functions from app-filter-options for product/noun building and auth splitting,
 * and delegates final visible/overflow slicing to {@link applyAppPillLayout}.
 */
function useLiteAppOptions(): { visibleOptions: AppOption[]; overflowOptions: AppOption[] } {
	const [{ products: availableProducts, nouns: availableNouns, thirdPartyConfigs }] =
		useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [isNounsAvailable] = useIsNounsAvailable();
	const userId = useCurrentUserId();
	const { recommended3pApps } = useRightPanelRecommendations({ userId, showRecommendations: true });
	const filters = useFilters();

	const { formatMessage } = useIntl();
	const products = useProductConfigs();

	// Derive the currently selected noun type from the universal type filter.
	// When a noun like "project" or "goal" is selected, the store sets product=townsquare
	// and sets the type filter to that noun value.
	const selectedNounType = useMemo(() => {
		const typeFilter = filters[universalTypeFilterId];
		if (typeFilter && 'selectedOptions' in typeFilter) {
			const selected = typeFilter.selectedOptions.find((opt) => opt.isSelected);
			return selected?.value;
		}
		return undefined;
	}, [filters]);

	return useMemo(() => {
		// Always exclude Townsquare/Atlas (rendered as "Home") from the Lite app pill bar.
		// In the right rail, Atlas is hidden because it's classified as a "nested product" of
		// the `atlas` product category and the category's primary product key (`'atlas'`) is
		// not a real ProductKeys value, so the parent never renders. The Lite filter bar does
		// not implement that nested-categories logic, so we drop Atlas explicitly here to keep
		// behaviour consistent with the right rail.
		const productsWithoutAtlas = availableProducts.filter((key) => key !== ProductKeys.Atlas);

		const shouldShowNouns = getShouldShowNouns(
			isNounsAvailable,
			availableNouns,
			productsWithoutAtlas,
		);
		const filteredProducts = filterProductsForNouns(productsWithoutAtlas, shouldShowNouns);

		const productOptions = buildProductOptions({
			filteredProducts,
			products,
			selectedProduct,
			thirdPartyConfigs,
		});

		const nounOptions = shouldShowNouns
			? buildNounOptions({
					availableNouns,
					availableProducts: productsWithoutAtlas,
					selectedProduct,
					selectedNounType,
					formatMessage,
				})
			: [];

		const allOptions = [...productOptions, ...nounOptions];
		const { authenticatedOptions, unauthenticatedOptions } = splitByAuthStatus(
			allOptions,
			thirdPartyConfigs,
		);

		// Rank unauthenticated 3P by recommendations order: walk the recommended list first
		// (preserving rank), then append any unauthenticated apps not in the recommendations list.
		const unauthenticatedByValue = new Map(unauthenticatedOptions.map((o) => [o.value, o]));
		const rankedFromRecommendations = recommended3pApps
			.map((key) => unauthenticatedByValue.get(key as ProductKeys | NounKeys))
			.filter((o): o is AppOption => o !== undefined);
		const recommendedKeys = new Set(recommended3pApps);
		const unranked = unauthenticatedOptions.filter((o) => !recommendedKeys.has(o.value as string));
		const rankedUnauthenticated3p = [...rankedFromRecommendations, ...unranked];

		// When the Team 26 Demo gate is on, pin a fixed front-of-bar order
		// (Jira → named 3Ps that are authenticated → Confluence) before slicing into the
		// visible/overflow buckets. Unauthenticated handling is unchanged: the named 3Ps that
		// are not connected fall back to the existing `rankedUnauthenticated3p` Connect-pill flow.
		const orderedAuthenticatedOptions = fg('rovo_fps_lite_app_filter_team26_demo_layout')
			? applyPinnedOrder(authenticatedOptions, TEAM26_PINNED_ORDER)
			: authenticatedOptions;

		// Pick the active layout (default vs Team 26 Demo) and split the options into visible vs
		// overflow. The layout choice is encapsulated in `getAppPillLayout()` and the slicing in
		// `applyAppPillLayout()` so this hook only owns option construction.
		return applyAppPillLayout({
			layout: getAppPillLayout(),
			authenticatedOptions: orderedAuthenticatedOptions,
			rankedUnauthenticated3p,
		});
	}, [
		availableProducts,
		availableNouns,
		isNounsAvailable,
		selectedProduct,
		selectedNounType,
		thirdPartyConfigs,
		products,
		formatMessage,
		recommended3pApps,
	]);
}

const AppPill = ({
	option,
	onSelect,
	isDisabled,
}: {
	option: AppOption;
	onSelect: (value: ProductKeys | NounKeys | undefined) => void;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const label = option.isUnauthenticated
		? formatMessage(
				{
					id: 'search_page.lite_app_filter_bar.connect_app',
					defaultMessage: 'Connect {app}',
					description: 'Label for an unauthenticated 3P app pill prompting the user to connect it',
				},
				{ app: option.label },
			)
		: option.label;

	return (
		<Pressable
			xcss={cx(styles.pill, option.isSelected && styles.pillSelected)}
			onClick={() => {
				if (!isDisabled) {
					onSelect(option.value);
				}
			}}
			aria-pressed={option.isSelected}
		>
			<Flex alignItems="center" gap="space.050">
				<Box xcss={styles.iconContainer}>
					<ProductOrNounIcon
						label=""
						size="xxsmall"
						productOrNounKey={option.value}
						isSelected={option.isSelected}
					/>
				</Box>
				<Box>{label}</Box>
				{option.isUnauthenticated && (
					<LinkExternalIcon label="" size="small" color={token('color.icon.subtle')} />
				)}
			</Flex>
		</Pressable>
	);
};

const AllPill = ({ isSelected, onSelect }: { isSelected: boolean; onSelect: () => void }) => {
	const { formatMessage } = useIntl();
	return (
		<Pressable
			xcss={cx(styles.pill, isSelected && styles.pillSelected)}
			onClick={onSelect}
			aria-pressed={isSelected}
		>
			{formatMessage({
				id: 'search_page.lite_app_filter_bar.all',
				defaultMessage: 'All',
				description: 'Label for the All filter pill that shows results from all apps',
			})}
		</Pressable>
	);
};

/**
 * Shared hook that returns a stable callback for selecting a product or noun filter.
 * Used by both LiteAppFilterBar (visible pills) and LiteShowAllAppsButton (overflow menu).
 */
function useLiteFilterSelect(): (value: ProductKeys | NounKeys | undefined) => void {
	const [selectedProduct] = useSelectedProduct();
	const {
		selectProductFilter,
		unselectProductFilter,
		unsafe_setAtlasNouns,
		unsafe_setWebPageType,
	} = useSearchActions();

	return useCallback(
		(value: ProductKeys | NounKeys | undefined) => {
			if (!value || value === selectedProduct) {
				unselectProductFilter();
				return;
			}
			if (value === NounKeys.AtlassianWebCrawler) {
				unsafe_setWebPageType();
				return;
			}
			if (isNounKey(value)) {
				unsafe_setAtlasNouns(value);
				return;
			}
			selectProductFilter(value as ProductKeys);
		},
		[
			selectedProduct,
			selectProductFilter,
			unselectProductFilter,
			unsafe_setAtlasNouns,
			unsafe_setWebPageType,
		],
	);
}

/**
 * Horizontal app filter bar for Lite mode. Shows "All" pill + up to 9 product pills
 * in a single horizontally-scrollable row. Chevron buttons appear on hover to scroll
 * left/right. A "Show all apps" dropdown provides access to overflow apps.
 */
export const LiteAppFilterBar = (): React.JSX.Element => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const isAllSelected = !selectedProduct;

	const { visibleOptions } = useLiteAppOptions();
	const handleSelect = useLiteFilterSelect();

	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		updateScrollState();
		const unbindScroll = bind(el, { type: 'scroll', listener: updateScrollState });
		const resizeObserver = new ResizeObserver(updateScrollState);
		resizeObserver.observe(el);
		return () => {
			unbindScroll();
			resizeObserver.disconnect();
		};
	}, [updateScrollState]);

	const computeScrollAmount = useCallback((direction: 'left' | 'right') => {
		const el = scrollRef.current;
		if (!el) {
			return 0;
		}

		const children = Array.from(el.children) as HTMLElement[];
		return getScrollAmountFromLayout(
			direction,
			{ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth },
			children.map((child) => ({ offsetLeft: child.offsetLeft, offsetWidth: child.offsetWidth })),
		);
	}, []);

	const scrollLeft = useCallback(() => {
		const amount = computeScrollAmount('left');
		scrollRef.current?.scrollBy({ left: -amount, behavior: 'smooth' });
	}, [computeScrollAmount]);

	const scrollRight = useCallback(() => {
		const amount = computeScrollAmount('right');
		scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
	}, [computeScrollAmount]);

	return (
		<Box
			xcss={styles.outerContainer}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			{isHovering && canScrollLeft && (
				<Box
					xcss={cx(styles.chevronContainer, styles.chevronLeft)}
					style={{
						background: `linear-gradient(to right, ${token('elevation.surface')} 50%, transparent)`,
						paddingInline: `${token('space.025')} ${token('space.200')}`,
					}}
				>
					<Box xcss={styles.chevronButton}>
						<IconButton
							appearance="default"
							spacing="compact"
							label="Scroll left"
							icon={ChevronLeftIcon}
							onClick={scrollLeft}
						/>
					</Box>
				</Box>
			)}

			<Box ref={scrollRef} xcss={styles.scrollContainer}>
				<AllPill isSelected={isAllSelected} onSelect={() => handleSelect(undefined)} />
				{visibleOptions.map((option) =>
					option.isUnauthenticated ? (
						<OAuthWarningWrapper
							key={option.value}
							product={option.value as ProductKeys}
							isSmartlink={isProductSmartlink({
								productKey: option.value,
								thirdPartyConfigs,
							})}
							useOAuthHandlers={useOAuthHandlers}
							useIsUserOAuthed={useIsUserOAuthed}
							oAuthSource={OAuthSource.searchPageFilter}
							thirdPartyConfigs={thirdPartyConfigs}
							skipFlagsProvider={false}
							trigger={({ isDisabled }: TriggerComponentProps) => (
								<AppPill option={option} onSelect={handleSelect} isDisabled={isDisabled} />
							)}
						/>
					) : (
						<AppPill key={option.value} option={option} onSelect={handleSelect} />
					),
				)}
			</Box>

			{isHovering && canScrollRight && (
				<Box
					xcss={cx(styles.chevronContainer, styles.chevronRight)}
					style={{
						background: `linear-gradient(to left, ${token('elevation.surface')} 50%, transparent)`,
						paddingInline: `${token('space.200')} ${token('space.025')}`,
					}}
				>
					<Box xcss={styles.chevronButton}>
						<IconButton
							appearance="default"
							spacing="compact"
							label="Scroll right"
							icon={ChevronRightIcon}
							onClick={scrollRight}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};

/**
 * "Show all apps" button that renders overflow app options beyond the 9 visible pills.
 * Designed to be placed alongside the "More filters" button in the header toggle area.
 */
export const LiteShowAllAppsButton = (): React.JSX.Element | null => {
	const [{ thirdPartyConfigs, cloudId, primaryProduct }] = useBootstrap();
	const { formatMessage } = useIntl();

	const { overflowOptions } = useLiteAppOptions();
	const handleSelect = useLiteFilterSelect();

	const showMoreOptions: FilterOption[] = useMemo(
		() =>
			overflowOptions.map((option) => ({
				label: option.label,
				value: option.value as ProductKeys | undefined,
				isSelected: option.isSelected,
				onSelect: () => handleSelect(option.value),
				hideFilter: () => {},
				showFilter: () => {},
			})),
		[overflowOptions, handleSelect],
	);

	const hasSelectedOverflow = showMoreOptions.some((opt) => opt.isSelected);

	if (showMoreOptions.length === 0) {
		return null;
	}

	const showAllAppsLabel = formatMessage({
		id: 'search_page.lite_app_filter_bar.show_all_apps',
		defaultMessage: 'Show all apps',
		description: 'Label for the dropdown that shows remaining app filters',
	});

	if (
		cloudId &&
		primaryProduct &&
		serveSearchSettingsModalExperiment({ fireExperimentExposure: false })
	) {
		return (
			<RovoSettingsModal
				product={primaryProduct}
				cloudId={cloudId}
				source={RovoSettingsModalSource.SEARCH}
				component={({ open }: { open: (options?: { defaultTab?: string }) => void }) => (
					<Button
						appearance="default"
						isSelected={hasSelectedOverflow}
						onClick={() => {
							triggerExposureForSearchSettingsModalExperiment();
							open({ defaultTab: 'discovery' });
						}}
					>
						{showAllAppsLabel}
					</Button>
				)}
			/>
		);
	}

	// Fire search experiment exposure for the control cohort at click-time, only
	// when this caller could have rendered the treatment branch above.
	const isEligibleForSearchSettingsModalExperiment = !!cloudId && !!primaryProduct;
	return (
		<ShowMoreAppsPopup
			options={showMoreOptions}
			useIsUserOAuthed={useIsUserOAuthed}
			useOAuthHandlers={useOAuthHandlers}
			thirdPartyConfigs={thirdPartyConfigs}
			oAuthSource={OAuthSource.searchPageFilter}
			onTriggerClick={
				isEligibleForSearchSettingsModalExperiment
					? triggerExposureForSearchSettingsModalExperiment
					: undefined
			}
			trigger={(triggerProps) => (
				<Button
					ref={triggerProps.ref as React.Ref<HTMLButtonElement>}
					appearance="default"
					isSelected={hasSelectedOverflow || triggerProps['aria-expanded'] === true}
					onClick={triggerProps.onClick}
					aria-expanded={triggerProps['aria-expanded']}
					aria-haspopup={triggerProps['aria-haspopup']}
				>
					{showAllAppsLabel}
				</Button>
			)}
		/>
	);
};
